/**
 * Echo Hunt -- the live (cached, profile-aware) seed-word list (P6).
 *
 * Per the ground rules (§16.0), the filtering logic lives in the pure `Wordlist.ts`; this thin
 * `*_Live.ts` wrapper is what (a) reuses the project's EXISTING word-list fetch/cache and (b) supplies
 * the user's profile settings and banned terms.
 *
 * ## Why it reuses the visualization loader
 *
 * `Resources/JSON/WordConcreteness_Sorted_Tagged.json` is already fetched + cached once per session by
 * `Engine/LiveSession/Components/Visualization/WordLists.ts` (for the lucid-dreaming visualization
 * prompt pools). Fetching the same 1.9 MB asset a second time for Echo Hunt would be wasteful and
 * would let the two features disagree about which words are concrete enough, so the loader is shared
 * rather than duplicated.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5.2, §6, §16.2 P6.
 */
import {
	GetWordList_MobX,
	WordEntry_Brysbaert,
} from "../Engine/LiveSession/Components/Visualization/WordLists.js";
import {EchoProfile} from "../Server/DB/echoProfiles/@EchoProfile.js";
import {
	FilterWordlist,
	FilterWordlistOptionsFromSettings,
	WordlistEntry,
	WordlistFilterOptions,
	WordlistFilterResult,
} from "./Wordlist.js";

/** Maps one row of the shared loader into this module's entry shape. */
export function ToWordlistEntry(entry: WordEntry_Brysbaert): WordlistEntry {
	return {
		word: entry.word,
		bigram: entry.bigram,
		concM: entry.concM,
		percentKnown: entry.percentKnown,
		subtlex: entry.subtlex,
		domPos: entry.domPos,
		tags: entry.tags,
	};
}

/** The parsed word list, or null while the shared one-time fetch is still in flight. Returning null
 * (rather than throwing) lets the UI show a spinner: the underlying loader is reactive, so an
 * observer re-renders once it lands. */
export function GetWordlistEntries(): WordlistEntry[]|n {
	const cached = GetWordList_MobX();
	return cached == null ? null : cached.map(ToWordlistEntry);
}

/** The filter options implied by the user's profile: their word-list cutoffs/tags plus their banned
 * terms (§4.4), which must never be offered as a seed either -- a banned term as a concept seed would
 * aim the fake straight at the one subject the corpus excludes. */
export function GetWordlistOptionsFromProfile(profile: EchoProfile|n): WordlistFilterOptions {
	return FilterWordlistOptionsFromSettings(profile?.wordlist ?? null, profile?.bannedTerms ?? []);
}

/** The filtered seed words + stats, or null while the word list is still loading. */
export function GetSeedWordlist(profile: EchoProfile|n): WordlistFilterResult|null {
	const entries = GetWordlistEntries();
	return entries == null ? null : FilterWordlist(entries, GetWordlistOptionsFromProfile(profile));
}
